import React, {useMemo, useState, useEffect, useCallback} from 'react';
import {Box, Button, CircularProgress, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableFooter, TableRow, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import axios from 'axios';
import {REPORTING_API_BASE, reportingHeaders} from '../../../utils/reportingApi';
import {NewPermissionGate} from '../../../permissions/permissionGate';
import {isIpermitUser} from '../../../permissions/utils';
import {companyRules, COMPANY_ELEMENTS} from '../../../permissions/companyAccessControl';
import MyPaginatedGrid from '../../../pieces/grids/paginatedGridWrapper';
import ColumnDefinition from '../../../pieces/grids/columnGenerator';
import DisplayListsPageWrapper from '../../wrappers/displayListsPageWrapper';
import ListPageHeader from '../../../pieces/listPageHeader';
import {ListViewHeader} from '../../jobs/components/listViewHeader';
import usePagination from '../../../hooks/usePagination';
import {useGridViewState} from '../../../hooks/useGridViewState';
import AccessDenied from '../../misc/accessDenied';
import {globalUseClasses} from '../../../styles/globalClasses';
import Utils from '../../../utils/utils';
import ModalWrapper, {ModalContentWrapper} from '../../../pieces/modals/modalWrapper';
import Loader from '../../misc/loader';
import {generateInvoicePdf} from '../../../utils/invoicePdf';
import {toast} from 'react-toastify';

const PAYMENT_STATUS_OPTIONS = [
  {value: -1, label: 'All'},
  {value: 1, label: 'Open'},
  {value: 0, label: 'Closed'},
];

const INVOICE_FIELDS = {
  JOB_NUMBER: {api_name: 'jobNumber', label: 'Job Number'},
  LINES_SUB_TOTAL: {api_name: 'linesSubTotal', label: 'Lines Sub Total'},
  BILL_EMAIL_ADDRESS: {api_name: 'billEmailAddress', label: 'QB Email'},
  DOC_NUMBER: {api_name: 'docNumber', label: 'Invoice Number'},
  PO: {api_name: 'po', label: 'PO#'},
  COMPANY_NAME: {api_name: 'companyName', label: 'Company'},
  CUSTOMER_ID: {api_name: 'customerID', label: 'Customer ID'},
  TXN_DATE: {api_name: 'txnDate', label: 'Invoice Date'},
  TOTAL_AMT: {api_name: 'totalAmt', label: 'Total Amount($)'},
  BALANCE: {api_name: 'balance', label: 'Balance($)'},
  CURRENCY_CODE: {api_name: 'currencyCode', label: 'Currency'},
  PRINT_STATUS: {api_name: 'printStatus', label: 'Print Status'},
  EMAIL_STATUS: {api_name: 'emailStatus', label: 'Email Status'},
};

const COLUMN_WIDTH_MEDIUM = 140;
const COLUMN_WIDTH_LARGE = 180;
const COLUMN_WIDTH_X_LARGE = 380;

const formatCurrency = (value) => {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  const absFormatted = Math.abs(num).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  return num < 0 ? `-$${absFormatted}` : `$${absFormatted}`;
};

const parseAmount = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
  const normalized = String(value).replace(/[$,\s]/g, '');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Normalize row from API (PascalCase or camelCase) to camelCase so column field names match */
const normalizeInvoiceRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  for (const key of Object.keys(row)) {
    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    out[camel] = row[key];
  }
  return out;
};

// Invoice data comes through the backend's reporting proxy, which limits it to
// the companies the user may see. See utils/reportingApi.js.
const invoiceApiBase = REPORTING_API_BASE;

/** The toast after "Refresh from QuickBooks", from the backend's RefreshSummary. */
const describeQuickBooksRefresh = (summary) => {
  const totals = summary?.totals ?? {};
  const customers = summary?.customers ?? [];
  const skipped = customers.filter((c) => c.skipped);
  if (customers.length === 0) {
    return {type: 'warning', text: 'These companies have no QuickBooks customer, so there is nothing to refresh.'};
  }
  if (skipped.length === customers.length) {
    return {type: 'info', text: `Not refreshed: ${skipped[0].skipped}.`};
  }
  const written = (totals.invoicesInserted ?? 0) + (totals.invoicesUpdated ?? 0);
  if (written === 0 && !totals.linksCreated) {
    return {type: 'success', text: `Already up to date with QuickBooks (${totals.scanned ?? 0} invoices checked).`};
  }
  const parts = [];
  if (totals.invoicesInserted) parts.push(`${totals.invoicesInserted} added`);
  if (totals.invoicesUpdated) parts.push(`${totals.invoicesUpdated} updated`);
  if (totals.linksCreated) parts.push(`${totals.linksCreated} linked to their jobs`);
  const notLinked = totals.notLinked ? ` ${totals.notLinked} could not be matched to a job.` : '';
  return {type: 'success', text: `Synced from QuickBooks: ${parts.join(', ')}.${notLinked}`};
};

export const InvoiceInfo = (props) => {

  const viewController = useGridViewState();
  const classes = globalUseClasses();
  const pagination = usePagination();
  const companyId = Utils.getUrlParameter('companyId') ?? '';
  const companyData = props.tabProps?.companyData ?? null;
  const invoiceCompanyIds = props.tabProps?.invoiceCompanyIds ?? '';

  const effectiveCompanyIds = invoiceCompanyIds || companyId;

  const [invPaymentStatus, setInvPaymentStatus] = useState(1); // 1 = Open (default)
  const [paymentStatusAnchor, setPaymentStatusAnchor] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [lineItems, setLineItems] = useState(null);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [lineItemsError, setLineItemsError] = useState(null);
  const [invoicesTotalAmount, setInvoicesTotalAmount] = useState(null);
  const [invoicesTotalBalance, setInvoicesTotalBalance] = useState(null);
  const [qbRefreshing, setQbRefreshing] = useState(false);
  // Staff only, as the backend route is.
  const canRefreshFromQuickBooks = isIpermitUser(viewController.userRole);

  const displayedLineItemsAmountTotal = useMemo(() => {
    if (!Array.isArray(lineItems?.items) || lineItems.items.length === 0) return 0;
    return lineItems.items.reduce((sum, item) => {
      const amount = item?.amount ?? item?.Amount;
      return sum + parseAmount(amount);
    }, 0);
  }, [lineItems]);

  const handleInvoiceFetchComplete = useCallback((data) => {
    if (!data) {
      setInvoicesTotalAmount(null);
      setInvoicesTotalBalance(null);
      return;
    }

    const total = data.invoicesTotal ?? data.InvoicesTotal;
    const balance = data.invoicesTotalBalance
      ?? data.InvoicesTotalBalance;
    
    setInvoicesTotalAmount(total != null ? Number(total) : null);
    setInvoicesTotalBalance(balance != null ? Number(balance) : null);
  }, []);

  const fetchInvoiceLineItems = useCallback(async (remoteID) => {
    if (!remoteID || !invoiceApiBase || !effectiveCompanyIds) return null;
    const url = `${invoiceApiBase}/Invoice/line-items/${encodeURIComponent(remoteID)}`;
    const headers = await reportingHeaders();
    const res = await axios.get(url, {headers, params: {companyIds: effectiveCompanyIds}});
    return res?.data ?? null;
  }, [effectiveCompanyIds]);

  useEffect(() => {
    if (!viewModalOpen || !selectedInvoice) {
      setLineItems(null);
      setLineItemsError(null);
      return;
    }
    const remoteID = selectedInvoice.remoteID ?? selectedInvoice.remoteId;
    if (!remoteID) {
      setLineItemsError('No invoice ID');
      return;
    }
    setLineItemsLoading(true);
    setLineItemsError(null);
    fetchInvoiceLineItems(remoteID)
      .then((data) => {
        setLineItems(data);
      })
      .catch((err) => {
        setLineItemsError(err?.response?.data?.message || err?.message || 'Failed to load line items');
        setLineItems(null);
      })
      .finally(() => {
        setLineItemsLoading(false);
      });
  }, [viewModalOpen, selectedInvoice, fetchInvoiceLineItems]);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedInvoice(null);
    setLineItems(null);
    setLineItemsError(null);
  }, []);

  const handleDownloadInvoice = useCallback(async (row) => {
    const remoteID = row?.remoteID ?? row?.remoteId;
    if (!remoteID) {
      toast.error('No invoice ID');
      return;
    }
    const toastId = toast.loading('Preparing invoice download...', {toastId: 'invoice-download'});
    try {
      const data = await fetchInvoiceLineItems(remoteID);
      toast.dismiss(toastId);
      if (data?.items?.length) {
        const docNumber = row?.docNumber ?? row?.DocNumber;
        generateInvoicePdf(data, docNumber);
        toast.success('Invoice downloaded');
      } else {
        toast.warning('No line items to download');
      }
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e?.response?.data?.message || e?.message || 'Failed to download invoice');
    }
  }, [fetchInvoiceLineItems]);

  const getContextMenuItems = useCallback((params) => {
    if (!params?.node) return [];
    const row = params.node.data;
    const actions = {
      view: {
        name: 'View',
        disabled: viewController.selectedMultipleRows?.() ?? false,
        cssClasses: ['bold'],
        action: () => {
          setSelectedInvoice(row);
          setViewModalOpen(true);
        },
      },
      download: {
        name: 'Download Invoice',
        disabled: viewController.selectedMultipleRows?.() ?? false,
        action: () => handleDownloadInvoice(row),
      },
    };
    const standard = [
      {name: 'Export Selected (.xlsx)', action: () => params.api.exportDataAsExcel({onlySelected: true})},
      'copy',
    ];
    return [actions.view, actions.download, 'separator', ...standard];
  }, [viewController, handleDownloadInvoice]);

  const companyNameFilterValues = useMemo(() => {
    if (!companyData || typeof companyData !== 'object') return [];

    const values = new Set();
    const parentCompanyNames = [
      companyData.companyName,
      companyData.name,
      companyData.parentCompany?.companyName,
      companyData.parentCompany?.name,
    ];
    parentCompanyNames.forEach((name) => {
      if (name != null && String(name).trim() !== '') {
        values.add(String(name).trim());
      }
    });

    const childCompanies = Array.isArray(companyData.childCompanies) ? companyData.childCompanies : [];
    childCompanies.forEach((childCompany) => {
      const childCompanyName = childCompany?.companyName ?? childCompany?.name;
      if (childCompanyName != null && String(childCompanyName).trim() !== '') {
        values.add(String(childCompanyName).trim());
      }
    });

    return Array.from(values);
  }, [companyData]);

  const columnDefs = useMemo(() => {
    const rawDefs = [
      new ColumnDefinition({
        field: INVOICE_FIELDS.JOB_NUMBER,
        width: COLUMN_WIDTH_MEDIUM,
        filter: 'agNumberColumnFilter',
        sortable: true
      }),
      new ColumnDefinition({field: INVOICE_FIELDS.DOC_NUMBER, sortable: true}),
      new ColumnDefinition({field: INVOICE_FIELDS.PO, width: COLUMN_WIDTH_LARGE, sortable: true}),
      new ColumnDefinition({
        field: INVOICE_FIELDS.COMPANY_NAME,
        width: COLUMN_WIDTH_X_LARGE,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterValues: companyNameFilterValues,
        wrapText: true,
        cellStyle: {whiteSpace: 'normal', lineHeight: 1.35, wordBreak: 'break-word'},
      }),
      new ColumnDefinition({
        field: INVOICE_FIELDS.TXN_DATE,
        filter: 'agDateColumnFilter',
        valueGetter: ({data}) => {
          const val = data?.[INVOICE_FIELDS.TXN_DATE.api_name];
          return val ? Utils.formatIsoDateString(val, {date: true, calendarDate: true}) : '';
        },
        sortable: true
      }),
      new ColumnDefinition({
        field: INVOICE_FIELDS.TOTAL_AMT,
        width: COLUMN_WIDTH_LARGE,
        valueGetter: ({data}) => formatCurrency(data?.[INVOICE_FIELDS.TOTAL_AMT.api_name]),
        cellStyle: (params) =>
          params.node?.rowPinned
            ? {fontWeight: 700, backgroundColor: '#fafafa'}
            : undefined,
        filter: 'agNumberColumnFilter',
        sortable: true
      }),
      new ColumnDefinition({
        field: INVOICE_FIELDS.BALANCE,
        width: COLUMN_WIDTH_MEDIUM,
        valueGetter: ({data}) => formatCurrency(data?.[INVOICE_FIELDS.BALANCE.api_name]),
        cellStyle: (params) =>
          params.node?.rowPinned
            ? {fontWeight: 700, backgroundColor: '#fafafa'}
            : undefined,
        filter: 'agNumberColumnFilter',
        sortable: true
      }),
      new ColumnDefinition({field: INVOICE_FIELDS.LINES_SUB_TOTAL, width: COLUMN_WIDTH_MEDIUM, hide: true, sortable: true}),
      new ColumnDefinition({field: INVOICE_FIELDS.BILL_EMAIL_ADDRESS, width: COLUMN_WIDTH_LARGE, sortable: true}),
    ];
    return rawDefs;
  }, [companyNameFilterValues]);

  const fetchParams = useMemo(() => {
    if (!invoiceApiBase || !effectiveCompanyIds) return null;
    return {
      url: `${invoiceApiBase}/Invoice/by-companyIds`,
      respKey: 'invoices',
      apiSource: 'iPermitPro',
      options: {invPaymentStatus, companyIds: effectiveCompanyIds},
    };
  }, [invoiceApiBase, invPaymentStatus, effectiveCompanyIds]);

  // Pulls what SyncHub or the invoice links are missing straight from QuickBooks,
  // then reloads the grid. A first run on a large customer can take a minute.
  const refreshFromQuickBooks = useCallback(async () => {
    if (!invoiceApiBase || !effectiveCompanyIds || qbRefreshing) return;
    setQbRefreshing(true);
    const toastId = toast.loading('Syncing invoices from QuickBooks...', {toastId: 'qb-invoice-refresh'});
    try {
      const headers = await reportingHeaders();
      const res = await axios.post(
        `${invoiceApiBase}/Invoice/refresh-from-quickbooks`,
        {companyIds: effectiveCompanyIds},
        {headers},
      );
      const {type, text} = describeQuickBooksRefresh(res?.data?.data);
      toast.update(toastId, {render: text, type, isLoading: false, autoClose: 8000});
      viewController.refreshGrid();
    } catch (e) {
      toast.update(toastId, {
        render: e?.response?.data?.message || e?.message || 'Could not sync invoices from QuickBooks',
        type: 'error',
        isLoading: false,
        autoClose: 8000,
      });
    } finally {
      setQbRefreshing(false);
    }
  }, [effectiveCompanyIds, qbRefreshing, viewController]);

  const paymentStatusOpen = Boolean(paymentStatusAnchor);
  const currentPaymentLabel = PAYMENT_STATUS_OPTIONS.find((o) => o.value === invPaymentStatus)?.label ?? 'Open';
  const openPaymentStatusMenu = (e) => setPaymentStatusAnchor(e.currentTarget);
  const closePaymentStatusMenu = () => setPaymentStatusAnchor(null);
  const setPaymentStatus = (value) => {
    setInvPaymentStatus(value);
    closePaymentStatusMenu();
    viewController.refreshGrid();
  };

  if (!NewPermissionGate({user: viewController.user, elementPermissions: companyRules[COMPANY_ELEMENTS.serviceTitanInfo]})) {
    return <AccessDenied />;
  }

  const headerComponent = (
    <ListPageHeader
      viewController={viewController}
      header=""
      showAddButton={false}
      headComponent={
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          columnGap={2}
          rowGap={1}
        >
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" flex="1 1 auto" minWidth={0}>
            <Typography variant="h4" component="span" sx={{lineHeight: 1.2}}>
              Invoices{pagination.totalItemsCount != null ? ` (${pagination.totalItemsCount})` : ''}
            </Typography>
            <Button
              variant="contained"
              onClick={openPaymentStatusMenu}
              endIcon={<ExpandMoreIcon />}
              sx={{backgroundColor: 'black', color: 'white', '&:hover': {backgroundColor: '#333'}, flexShrink: 0}}
            >
              {currentPaymentLabel}
            </Button>
            <Menu
              anchorEl={paymentStatusAnchor}
              open={paymentStatusOpen}
              onClose={closePaymentStatusMenu}
              anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
              transformOrigin={{vertical: 'top', horizontal: 'left'}}
            >
              {PAYMENT_STATUS_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.value}
                  onClick={() => setPaymentStatus(opt.value)}
                  sx={{color: 'Black', mt: 0.25, mb: 0.25, pt: 0, pb: 0}}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>
            {canRefreshFromQuickBooks && (
              <Button
                variant="outlined"
                onClick={refreshFromQuickBooks}
                disabled={qbRefreshing || !effectiveCompanyIds}
                startIcon={qbRefreshing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                sx={{color: 'black', borderColor: 'black', flexShrink: 0, '&:hover': {borderColor: '#333', backgroundColor: '#f5f5f5'}}}
              >
                {qbRefreshing ? 'Syncing...' : 'Sync from QuickBooks'}
              </Button>
            )}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="flex-end" flexShrink={0} sx={{ml: {xs: 0, md: 1}}}>
            <ListViewHeader title="" viewController={viewController} pagination={pagination} />
          </Box>
        </Box>
      }
    >
      {pagination.ItemsController}
    </ListPageHeader>
  );

  const showGrid = Boolean(effectiveCompanyIds && fetchParams);
  const showNoApiUrl = companyId && !invoiceApiBase;

  const hasInvoicesFooterTotals =
    ((invoicesTotalAmount != null && !Number.isNaN(invoicesTotalAmount))
      || (invoicesTotalBalance != null && !Number.isNaN(invoicesTotalBalance)));

  const pinnedBottomRowData = hasInvoicesFooterTotals
    ? [{
      // Keep these as numbers; the grid's valueGetters handle currency formatting.
      totalAmt: invoicesTotalAmount ?? 0,
      balance: invoicesTotalBalance ?? 0,
    }]
    : [{
      totalAmt: 0,
      balance: 0,
    }];

  const content = (
    <Box className={classes.pageInsideTab}>
      <DisplayListsPageWrapper headerComponent={headerComponent}>
        {showNoApiUrl && (
          <Box sx={{p: 2, color: 'text.secondary'}}>
            Invoice API URL is not configured. Set REACT_APP_API_BASE_URL in .env and restart the dev server.
          </Box>
        )}
        {viewModalOpen && (
          <ModalWrapper
            open={viewModalOpen}
            handleClose={handleCloseViewModal}
            modalName="invoice-view"
            customStyle={{minHeight: 'auto'}}
            preventScroll
            width={{xs: '98%', sm: '700px'}}
            padding={4}
          >
            <ModalContentWrapper handleClose={handleCloseViewModal} header="Invoice Details">
              {lineItemsLoading && (
                <Box sx={{position: 'relative', minHeight: 320}}>
                  <Loader useOwnWrapper timeout={0} />
                </Box>
              )}
              {lineItemsError && (
                <Typography color="error">{lineItemsError}</Typography>
              )}
              {!lineItemsLoading && !lineItemsError && lineItems?.items?.length > 0 && (
                <>
                  <Box sx={{display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap'}}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Bill To</Typography>
                      <Typography variant="body2">
                        {[lineItems.items[0].billAddrLine1 ?? lineItems.items[0].BillAddrLine1, lineItems.items[0].billAddrLine2 ?? lineItems.items[0].BillAddrLine2, lineItems.items[0].billAddrLine3 ?? lineItems.items[0].BillAddrLine3].filter(Boolean).join(', ') || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Ship To</Typography>
                      <Typography variant="body2">
                        {[lineItems.items[0].shipAddrLine1 ?? lineItems.items[0].ShipAddrLine1, lineItems.items[0].shipAddrLine2 ?? lineItems.items[0].ShipAddrLine2, lineItems.items[0].shipAddrLine3 ?? lineItems.items[0].ShipAddrLine3].filter(Boolean).join(', ') || '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <TableContainer sx={{mb: 2}}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Line #</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Unit Price($)</TableCell>
                          <TableCell align="right">Amount($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lineItems.items.map((item, idx) => (
                          <TableRow key={item.itemID ?? item.itemId ?? idx}>
                            <TableCell>{item.lineNum ?? item.LineNum}</TableCell>
                            <TableCell>{item.name ?? item.Name ?? ''}</TableCell>
                            <TableCell align="right">{item.qty ?? item.Qty ?? 0}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unitPrice ?? item.UnitPrice)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.amount ?? item.Amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={4} align="right" sx={{fontWeight: 700, color: 'black', fontSize: '1rem'}}>Total</TableCell>
                          <TableCell align="right" sx={{fontWeight: 700, color: 'black', fontSize: '1rem'}}>{formatCurrency(displayedLineItemsAmountTotal)}</TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                  <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button variant="contained" color="primary" onClick={() => generateInvoicePdf(lineItems, selectedInvoice?.docNumber ?? selectedInvoice?.DocNumber)}>
                      Download Invoice
                    </Button>
                  </Box>
                </>
              )}
              {!lineItemsLoading && !lineItemsError && lineItems?.items?.length === 0 && (
                <Typography color="textSecondary">No line items.</Typography>
              )}
            </ModalContentWrapper>
          </ModalWrapper>
        )}
        {showGrid && (
          <>
            <MyPaginatedGrid
              columnDefs={columnDefs}
              ls_name="company_invoices_grid"
              registerGrid={viewController.setGridToParent}
              registerTempGrid={viewController.setTempGridToParent}
              fetchParams={fetchParams}
              companyId={companyId}
              modifyDataElement={normalizeInvoiceRow}
              onFetchFail={viewController.onFetchFail}
              onServerFetchComplete={handleInvoiceFetchComplete}
              pagination={pagination}
              refreshTimestamp={viewController.refreshTimestamp}
              isTabOffset={true}
              contextMenu={getContextMenuItems}
              pinnedBottomRowData={pinnedBottomRowData}
            />
            {pagination.PageController}
          </>
        )}
      </DisplayListsPageWrapper>
    </Box>
  );

  return viewController.RenderElement(content);
};

export default InvoiceInfo;
